import React, { useState, FormEvent } from 'react';
import { ContactFormData, ApiResponse } from './types';
import './css/CTA.css';

const CTA: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    message: ''
  });
  const [submittedData, setSubmittedData] = useState<ContactFormData | null>(null);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | '';
    message: string;
  }>({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const text = await response.text();
      let data: ApiResponse;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (err) {
        setStatus({
          type: 'error',
          message: 'Invalid response from server.'
        });
        setIsSubmitting(false);
        return;
      }

      if (response.ok && data && data.success) {
        setStatus({
          type: 'success',
          message: "Message sent successfully! We'll be in touch soon."
        });
        setSubmittedData(formData);
        setFormData({ name: '', email: '', message: '' });
      } else if (response.ok) {
        setStatus({
          type: 'success',
          message: "Message sent successfully!"
        });
        setSubmittedData(formData);
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error((data && data.error) || 'Failed to send message');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Failed to send message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="cta-section" id="contact">
      <div className="container">
        <div className="fade-in">
          <h2>Ready to Transform Your Infrastructure?</h2>
          <p>Let's discuss how we can help you achieve your cloud and development goals</p>
          
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {status.message && (
                <div className={`form-message ${status.type}`}>
                  {status.message}
                  {submittedData && (
                     <div>
                       <p>Email: {submittedData.email}</p>
                       <p>Name: {submittedData.name}</p>
                       <p>Message: {submittedData.message}</p>
                     </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
